import './index.css'

import { LuArrowUpFromLine } from '@react-icons/all-files/lu/LuArrowUpFromLine'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import { ALBUMS } from '@disco/constants/FeatureTypes'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import {
  CUSTOM_CHANNEL,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import type { PaneType } from '@disco/constants/PaneTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
} from '@disco/constants/SortTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  DEFAULT_VIEW,
  TRACK_VIEW,
} from '@disco/constants/ViewTypes'
import {
  deleteGroup,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import {
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import type { SimSearchQuery } from '@disco/state-management/reducers/search'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getIsSubfolder,
  makeGetGroupLocks,
} from '@disco/state-management/selectors/groups'
import { numHiddenShortlistUploads } from '@disco/state-management/selectors/shortlists'
import { getUser } from '@disco/state-management/selectors/user'
import { getSearchOptions } from '@disco/utils/search'

import type { Group, GroupId } from 'types/group'
import type { V2SearchQuery } from 'types/search-page'
import type { PopoverItemType } from 'types/ui'

import { AccessibleIcon, Flex, IconButton } from 'components/ds'
import GroupItemActions from 'components/ellipsis-menus/group-item-actions'
import TagMenu from 'components/explorer/tag-menu'
import ButtonContainer from 'components/ui/button-container'
import Tooltip from 'components/ui/tooltip'

import type { RootState } from 'store/configureStore'

import { BrowseSortMenu } from './browse-sort-menu'
import { SearchSortMenu } from './search-sort-menu'
import {
  artistOptions,
  channelPlaylistOptions,
  playlistOptions,
  tagFilterOption,
  trackOptions,
} from './select-options'

const mapStateToProps = (state: RootState, { group }: { group: Group }) => {
  const getGroupLocks = makeGetGroupLocks()
  const isFeatureEnabled = makeIsFeatureEnabled()
  return {
    user: getUser(state),
    searchType: state.search.type,
    disableDestructiveActions: group
      ? getGroupLocks(state, {
          groupId: group.id,
        })?.lock_structure
      : false,
    albumsEnabled: isFeatureEnabled(state, {
      featureName: ALBUMS,
    }),
    numHiddenShortlistUploads: numHiddenShortlistUploads(state),
    isSubfolder: group
      ? getIsSubfolder(state, {
          groupId: group.id,
        })
      : false,
  }
}

const connector = connect(mapStateToProps, {
  getSearchResults,
  updateSearchParams,
  updateGroup,
  deleteGroup,
})

type ReduxProps = ConnectedProps<typeof connector>

type Props = ReduxProps & {
  pane: {
    groupType: GROUP_TYPE
    groupId: GroupId
    artistSortParam: SORT | null | undefined
    artistSortDirection: SORT_DIRECTION | null | undefined
    trackSortParam: SORT | null | undefined
    trackSortDirection: SORT_DIRECTION | null | undefined
    playlistSortParam: SORT
    playlistSortDirection: SORT_DIRECTION
    view: ViewType | null | undefined
    type: PaneType
    index: number
    simSearch: SimSearchQuery | null | undefined
    searchQueryV2?: V2SearchQuery
    meta: Record<string, any>
  }
  group: Group | null | undefined
  groupDepth: number
  searchType: string
  onViewSwitch: (view: ViewType | null | undefined, value?: string) => unknown
  filterTags: Array<string>
  onTagAdd: (...args: Array<any>) => any
  onTagRemove: (...args: Array<any>) => any
  searchSort: string
  searchSortDirection: string
  copyButton: React.ReactNode
  isTrial: boolean
  navigate: NavigateFunction
}

class SearchPaneHeader extends React.Component<
  Props,
  {
    tagMenuOpen: boolean
  }
> {
  state = {
    tagMenuOpen: false,
  }
  _sortOptions: HTMLElement | null | undefined
  _onOptionChoose = (value: string) => {
    const { onViewSwitch, pane } = this.props

    if (value !== tagFilterOption.value) {
      onViewSwitch(pane.view, value)
    } else {
      this._tagMenuSwitch()
    }
  }
  _tagMenuSwitch = () =>
    this.setState({
      tagMenuOpen: !this.state.tagMenuOpen,
    })

  _selectedSortValue(): string | null {
    const { pane } = this.props

    if (
      pane.view === ARTIST_VIEW &&
      pane.artistSortParam &&
      pane.artistSortDirection
    ) {
      return `${pane.artistSortParam} ${pane.artistSortDirection}`
    }
    if (
      pane.view === TRACK_VIEW &&
      pane.trackSortParam &&
      pane.trackSortDirection
    ) {
      return `${pane.trackSortParam} ${pane.trackSortDirection}`
    }
    if (pane.playlistSortParam && pane.playlistSortDirection) {
      return `${pane.playlistSortParam} ${pane.playlistSortDirection}`
    }
    return null
  }

  _rawSortOptions() {
    const { pane } = this.props

    let options = [CUSTOM_CHANNEL, SAVED_TO_DISCO].includes(pane.groupType)
      ? channelPlaylistOptions
      : playlistOptions

    if (pane.view === ARTIST_VIEW) options = artistOptions
    if (pane.view === TRACK_VIEW) options = trackOptions

    return options
  }

  _sortingOptions(): PopoverItemType[] {
    const selectedValue = this._selectedSortValue()

    return this._rawSortOptions().map(option => {
      if (selectedValue === option.value)
        return {
          selected: true,
          ...option,
        }
      return option
    })
  }

  _renderSearchEllipsis() {
    const { pane, searchSort, searchSortDirection, updateSearchParams } =
      this.props
    const options = getSearchOptions(this.props.searchType)
    const queryType = pane.searchQueryV2?.type
    if (queryType === 'simsearch' || queryType === 'llm') {
      return this._renderAiSearchEllipsis(options, pane.searchQueryV2?.sort)
    }
    return (
      <div className="ExplorerPane_Header_Ellipsis">
        <SearchSortMenu
          options={options}
          searchSort={searchSort}
          searchSortDirection={searchSortDirection}
          onSelect={(sort, sortDirection) => {
            updateSearchParams({
              sort: sort.replace('-', ''),
              sortDirection,
            })
          }}
        />
      </div>
    )
  }

  // Sim/prompt results show the sort they were fetched with and re-sort
  // directly, leaving the keyword sort preference untouched.
  _renderAiSearchEllipsis(
    options: ReturnType<typeof getSearchOptions>,
    sort: string[] | undefined,
  ) {
    const [appliedSort] = sort ?? []
    return (
      <div className="ExplorerPane_Header_Ellipsis">
        <SearchSortMenu
          options={options}
          searchSort={appliedSort ? appliedSort.replace('-', '') : 'score'}
          searchSortDirection={
            !appliedSort || appliedSort.startsWith('-')
              ? SORT_DIRECTION_DESC
              : SORT_DIRECTION_ASC
          }
          onSelect={(newSort, sortDirection) => {
            this.props.getSearchResults('', {
              sort: newSort.replace('-', ''),
              sortDirection: sortDirection as SORT_DIRECTION,
            })
          }}
        />
      </div>
    )
  }

  _renderSavedToDiscoEllipsis() {
    return (
      <div className="ExplorerPane_Header_Ellipsis">
        <BrowseSortMenu
          options={this._rawSortOptions()}
          selectedValue={this._selectedSortValue()}
          onOptionClick={this._onOptionChoose}
        />
      </div>
    )
  }

  render() {
    const { pane, filterTags, onTagAdd } = this.props
    if (pane.simSearch && pane.searchQueryV2?.type !== 'simsearch') return null

    if (pane.type === SEARCH_RESULTS_PANE && !pane.groupId) {
      return this._renderSearchEllipsis()
    }

    if (pane.groupType === CUSTOM_CHANNEL) {
      return (
        <GroupItemActions
          pane={this.props.pane}
          group={this.props.group}
          groupDepth={this.props.groupDepth}
          sortingOptions={this._sortingOptions()}
          onViewSwitch={this.props.onViewSwitch}
        />
      )
    }

    if (pane.groupType === SAVED_TO_DISCO) {
      return this._renderSavedToDiscoEllipsis()
    }

    if (pane.type !== SEARCH_RESULTS_PANE || !pane.groupId) {
      return null
    }

    const sortOptions = (
      <div
        className="ExplorerPane_Header_SortOptions"
        ref={r => {
          this._sortOptions = r
        }}
      >
        <BrowseSortMenu
          options={this._rawSortOptions()}
          selectedValue={this._selectedSortValue()}
          onOptionClick={this._onOptionChoose}
        />
      </div>
    )
    const showImport = [TEAM_LIBRARY, PERSONAL_LIBRARY].includes(pane.groupType)
    const importOption = this.props.numHiddenShortlistUploads ? (
      <Tooltip content={`${this.props.numHiddenShortlistUploads} remaining`}>
        <div className="ExplorerPane_Header_Spinner" />
      </Tooltip>
    ) : (
      <IconButton
        className="SearchHeader_IconButton"
        variant="ghost"
        size="2"
        color="gray"
        highContrast
        onClick={() => {
          this.props.navigate('/file-import')
        }}
      >
        <AccessibleIcon label="Import file">
          <LuArrowUpFromLine size={18} />
        </AccessibleIcon>
      </IconButton>
    )
    return (
      <div className="flc">
        <ButtonContainer>
          {this.props.copyButton}
          <Flex gap="3" align="center">
            {showImport && importOption}
            {sortOptions}
          </Flex>
        </ButtonContainer>
        {(!pane.view || [DEFAULT_VIEW, ALBUM_VIEW].includes(pane.view)) &&
          this._sortOptions &&
          this.state.tagMenuOpen && (
            <TagMenu
              align="right"
              position={this._sortOptions.getBoundingClientRect()}
              selectedTags={filterTags}
              tagType="playlist"
              onClose={this._tagMenuSwitch}
              onSelect={onTagAdd}
            />
          )}
      </div>
    )
  }
}

const ConnectedSearchPaneHeader = connector(SearchPaneHeader)

const SearchPaneHeaderWithNavigation = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedSearchPaneHeader {...props} navigate={navigate} />
}

export default SearchPaneHeaderWithNavigation
